import React, { useState, useRef, useCallback, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import { withNamespaces } from "react-i18next";
import { Button, TextField, Typography } from "@material-ui/core";
import moment from "moment";
import { Snackbar, useTheme } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";

import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import Web3 from "web3";

import { formatBytes32String } from "ethers/utils";
import DeleteIcon from "@material-ui/icons/Delete";

import EthereumIcon from "../../assets/eth-blue.png";
import PolygonIcon from "../../assets/polygon.svg";
import BscIcon from "../../assets/bsc.svg";
import AvaxIcon from "../../assets/avax.svg";
import PulseIcon from "../../assets/PulseChain.png";
import BaseIcon from "../../assets/logos/base-logo.svg";
import {
  APPLY,
  APPLY_RETURNED,
  CONNECTION_CONNECTED,
  ERROR,
  GET_BALANCES,
  GET_BALANCES_RETURNED,
  GET_DEV_FEES,
  GET_DEV_FEES_RETURNED,
  GET_MIN_CREATOR_STAKED_BALANCE,
  GET_MIN_CREATOR_STAKED_BALANCE_RETURNED,
  defaultFundingAddress,
  fundingTokens,
  toFixed,
  toWeiString,
  tokens,
} from "../../constants/constants";
import Store from "../../stores/store";
import ConfirmationModal from "../confirmationModal";
import { useHistory } from "react-router-dom";
import { validateUserBalance } from "../../utils/validateUserBalance";
import bigInt from "big-integer";
import { shortenUrl } from "../../utils/shortenUrl";
import { api } from "../stake/api";
import { useAccount } from "../../contexts/AccountContext";
import { getTokenDecimals } from "../../utils/getTokenDecimals";

const store = Store.store;
const dispatcher = Store.dispatcher;
const emitter = Store.emitter;

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const presaleType = {
  tokenSale: "0",
  communityIdo: "1",
  degenMode: "2",
};

const categoryIds = [
  "DeFi",
  "Farming",
  "Games",
  "Gambling",
  "Exchanges",
  "Collectibles",
  "Marketplaces",
  "Social",
  "Other",
];

const releaseCycles = [
  {
    label: "Daily",
    value: 86400,
  },
  {
    label: "Weekly",
    value: 604800,
  },
  {
    label: "Bi-Monthly",
    value: 1296000,
  },
  {
    label: "Monthly",
    value: 2592000,
  },
  {
    label: "Quarterly",
    value: 7776000,
  },
  {
    label: "Bi-Annually",
    value: 15552000,
  },
  {
    label: "Yearly",
    value: 31536000,
  },
];

const saleTypeIds = ["Seed", "Private", "Strategic", "Public"];
const dexIds = ["Aerodrome", "Uniswap"];

const styles = (theme) => ({
  root: {
    flex: 1,
    display: "flex",
    gap: "40px",
    width: "100%",
    maxWidth: "calc(1140px + 60px)",
    alignItems: "flex-start",
    flexDirection: "column",
    padding: "30px",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      maxWidth: "calc(1140px + 140px)",
      padding: "70px",
    },
    fontFamily: "Poppins",
    position: "relative",
  },
  goBackContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "auto",

    marginBottom: "16px",
    [theme.breakpoints.up("sm")]: {
      marginBottom: "24px",
    },
  },
  goBackIcon: {
    stroke: theme?.colors?.presale?.cardTitle,
    width: "24px",
    height: "24px",
    cursor: "pointer",
  },
  goBackBtn: {
    color: theme?.colors?.presale?.cardTitle,
    backgroundColor: "transparent",
    border: "none",
    fontWeight: "600",
    fontSize: "18px",
    cursor: "pointer",

    [theme.breakpoints.up("sm")]: {
      fontSize: "22px",
    },
  },
  checkboxGroup: {
    color: theme?.colors?.createPool?.subTitleText,
  },
  customCheckbox: {
    color: theme?.colors?.createPool?.inputLabel, // Set the checkbox color
    "&.Mui-checked": {
      color: theme?.colors?.wallet?.buttonBackground, // Set the color when checked
    },
  },
  customRadio: {
    color: theme.colors.createPool.inputLabel, // Set the radio button color
    "&.Mui-checked": {
      color: theme.colors.wallet.buttonBackground, // Set the color when checked
    },
  },
  poolsTitleContainer: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    width: "100%",
    maxWidth: "714px",
  },
  networkContainer: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    width: "100%",
    maxWidth: "1140px",
  },
  networkInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",
    alignItems: "center",
    marginTop: "24px",
    "& p": {
      color: theme.colors.networkContent.textColor,
    },

    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },

    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  },
  networkContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    background: theme.colors.networkContent.backgroundColor,
    borderRadius: "20px",
    padding: "2rem",
    cursor: "pointer",
    "& img": {
      width: "8rem",
      height: "8rem",
      objectFit: "scale-down",
    },
  },
  projectRequirements: {
    display: "none",
    [theme.breakpoints.up("lg")]: {
      display: "block",
    },
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "40px",
    background: theme?.colors?.createPool?.contentContainerBackground,
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "20px",
    padding: "32px",
    overflow: "auto",
  },
  privateProjectContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "40px",
    background: theme?.colors?.createPool?.contentContainerBackground,
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "20px",
    padding: "32px",
    overflow: "auto",
    gap: "40px",
  },
  whitelistContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",
    alignItems: "center",
    marginTop: "24px",
    "& p": {
      color: "#A0A0A0",
    },
  },
  poolTypeInnerContent: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    background: theme?.colors?.createPool?.contentContainerBackground,
    boxShadow: "0px 10px 60px " + theme?.colors?.containerShadow,
    borderRadius: "20px",
    padding: "32px",
    cursor: "pointer",
  },
  contactInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",

    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  listingInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",

    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  infoInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",

    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  tokenInfoInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",

    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "auto 10rem",
    },
  },
  tokenImgInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  poolsLabel: {
    fontFamily: "Poppins",
    fontSize: "40px",
    fontWeight: "600",
    textAlign: "left",
    letterSpacing: "0px",
    color: theme.colors.networkContent.title,

    [theme.breakpoints.up("lg")]: {
      height: "65px",
    },
  },
  networkSubtitle: {
    fontSize: "20px",
    marginBottom: "16px",
    fontWeight: "medium",
    textAlign: "left",
    color: "#A0A0A0",
  },
  subTitleText: {
    fontSize: "20px",
    marginBottom: "16px",
    fontWeight: "medium",
    textAlign: "left",
    color: theme?.colors?.createPool?.subTitleText,
  },
  inputLabel: {
    fontSize: "15px",
    marginTop: "0",
    marginBottom: "16px",
    fontWeight: "medium",
    textAlign: "left",
    color: theme?.colors?.createPool?.inputLabel,
  },
  poolTypeTitle: {
    fontSize: "15px",
    marginTop: "0",
    marginBottom: "16px",
    fontWeight: "600",
    textAlign: "left",
    color: theme?.colors?.createPool?.inputLabel,
  },
  inputRequired: {
    color: "#ff0000",
  },
  tokenPriceContainer: {
    position: "relative",
    display: "flex",
    background: "transparent",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,

    color: "#fff",
    width: "100%",
    borderRadius: "20px",
    marginBottom: "26px",
  },
  tokenPriceInput: {
    flex: "1",
    background: "transparent",
    border: "none",
    borderRight: "2px solid" + theme?.colors?.createPool?.inputBorder,

    color: theme?.colors?.createPool?.inputLabel,
    width: "100%",
    [theme.breakpoints.up("md")]: {
      padding: "0 16px",
    },
  },
  tokenPriceInUSD: {
    color: theme?.colors?.createPool?.inputLabel,
    width: "5.5rem",
    textAlign: "center",

    [theme.breakpoints.up("md")]: {
      width: "8rem",
    },
  },
  tokenNetworkImg: {
    position: "absolute",
    bottom: "14px",
    left: "12px",
    height: "20px",
    width: "20px",
    [theme.breakpoints.up("md")]: {
      height: "25px",
      width: "25px",
      bottom: "12px",
    },
  },
  tokenPriceSelect: {
    width: "7rem",
    borderTopLeftRadius: "20px",
    borderBottomLeftRadius: "20px",
    background: "transparent",
    color: theme?.colors?.createPool?.inputLabel,

    padding: "12px 24px 12px 36px",
    border: "none",
    borderRight: "2px solid" + theme?.colors?.createPool?.inputBorder,

    "& option": {
      color: "#000",
    },

    [theme.breakpoints.up("md")]: {
      width: "8rem",
      padding: "12px 8px 12px 46px",
    },
  },
  categorySelect: {
    background: "transparent",

    "& option": {
      color: "#000",
    },

    border: "2px solid" + theme?.colors?.createPool?.inputBorder,
    color: theme?.colors?.createPool?.inputLabel,

    width: "100%",
    borderRadius: "20px",
    padding: "16px",
  },
  infoText: {
    fontSize: "17px",
    margin: "0",
    fontWeight: "normal",
    textAlign: "left",
    color: "#A0A0A0",
  },
  galleryImgGrid: {
    width: "fit-content",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: "40px",
    gridRowGap: "40px",
    marginTop: "30px",
  },
  galleryImg: {
    width: "100%",
    height: "100%",
    borderRadius: "10px",
    objectFit: "cover",
    [theme.breakpoints.up("md")]: {
      borderRadius: "20px",
    },
  },
  galleryImgContainer: {
    position: "relative",

    border: "2px solid" + theme?.colors?.createPool?.inputBorder,
    background: "transparent",
    width: "5rem",
    height: "5rem",
    borderRadius: "10px",

    [theme.breakpoints.up("md")]: {
      width: "98px",
      height: "98px",
      borderRadius: "20px",
    },
  },
  tokenImgInput: {
    margin: "24px 0 0",
  },
  tokenImgContainer: {
    position: "relative",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,

    background: "transparent",
    width: "230px",
    height: "230px",
    borderRadius: "50%",
    margin: "0 auto",

    [theme.breakpoints.up("md")]: {
      width: "187px",
      height: "187px",
      marginLeft: "auto",
    },
  },
  tokenImg: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
  },
  whitelistFileNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  whitelistImgInput: {
    margin: "24px 0 0",
  },
  mainImgInput: {
    margin: "24px 0 0",
    objectFit: "cover",
  },
  galleryImgInput: {
    margin: "24px 0 30px",
  },
  mainImgContainer: {
    position: "relative",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,
    background: "transparent",
    width: "187px",
    height: "187px",
    borderRadius: "20px",
    marginLeft: "auto",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  mainImg: {
    width: "100%",
    height: "100%",
    borderRadius: "20px",
    objectFit: "cover",
  },
  projectInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",
    marginBottom: "30px",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  createButton: {
    borderRadius: "24px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: theme?.colors?.wallet?.buttonBackground,
    color: theme?.colors?.wallet?.buttonTextSecondary,
    textTransform: "capitalize",
    height: "60px",
    width: "100%",
    padding: "8px 16px",
    margin: "24px auto 0",
    "&:hover": {
      backgroundColor: theme?.colors?.wallet?.buttonBackgroundHover,
      color: theme?.colors?.wallet?.buttonTextSecondary,
    },

    [theme.breakpoints.up("md")]: {
      width: "217px",
    },
  },
  imgGalleryBtn: {
    borderRadius: "24px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: theme?.colors?.wallet?.buttonBackground,
    color: theme?.colors?.wallet?.buttonTextSecondary,
    textTransform: "capitalize",
    height: "60px",
    width: "217px",
    padding: "8px 16px",
    margin: "24px auto 0 0",
    "&:hover": {
      backgroundColor: theme?.colors?.wallet?.buttonBackgroundHover,
      color: theme?.colors?.wallet?.buttonTextSecondary,
    },
  },
  whitelistBtn: {
    borderRadius: "24px",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    backgroundColor: theme?.colors?.wallet?.buttonBackground,
    color: theme?.colors?.wallet?.buttonTextSecondary,
    textTransform: "capitalize",
    height: "60px",
    width: "217px",
    padding: "8px 16px",
    marginRight: "auto",
    "&:hover": {
      backgroundColor: theme?.colors?.wallet?.buttonBackgroundHover,
      color: theme?.colors?.wallet?.buttonTextSecondary,
    },
  },
  buttonLabel: {
    fontWeight: "bold",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
  },
  textInput: {
    background: "transparent",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,
    color: theme?.colors?.createPool?.inputLabel,

    width: "100%",
    borderRadius: "20px",
    padding: "16px",
  },
  inputDatetime: {
    background: "transparent",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,

    width: "100%",
    borderRadius: "20px",
    padding: "16px",
    color: theme?.colors?.createPool?.inputLabel + " !important",

    "& .MuiInputBase-root": {
      color: theme?.colors?.createPool?.inputLabel + " !important",
    },

    colorScheme: theme?.colors?.createPool?.datetimeSchema,
  },
  descriptionTextArea: {
    background: "transparent",
    border: "2px solid" + theme?.colors?.createPool?.inputBorder,
    color: theme?.colors?.createPool?.inputLabel,

    width: "100%",
    borderRadius: "20px",
    padding: "16px",
  },
  descriptionContainer: {
    marginTop: "40px",
  },

  poolTypeContainer: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridColumnGap: "30px",
    gridRowGap: "40px",
    marginBottom: "40px",
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "222px 222px 222px",
    },
  },
  poolInnerContent: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridRowGap: "40px",
  },
  voteDescription: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingLeft: "6px",
    gap: "4px",

    "& > li::marker": {
      color: theme?.colors?.presale?.commentText,
    },
  },
  voteTipText: {
    color: theme?.colors?.presale?.commentText,
    fontSize: "17px",
    fontWeight: "normal",
    lineHeight: "25px",
  },
});

const getFieldsToValidate = (poolType) => {
  let fields = [
    { key: "selectedNetwork", message: "No network was selected." },
    { key: "poolTitle", message: "Project Title is required." },
    { key: "tokenAddress", message: "ERC20 Token Address is required." },
    { key: "tokenDecimals", message: "Token Decimals is required." },
    { key: "tokenImg", message: "Token Image is required." },
    { key: "tokenPriceInWei", message: "Token Price is required." },
    { key: "releaseCycle", message: "Release Cycle is required." },
    { key: "releaseAtTGE", message: "Release at TGE (%) is required." },
    { key: "releasePerCycle", message: "Release Per Cycle (%) is required." },
    { key: "releaseCliff", message: "Vesting Cliff (Days) is required." },
    {
      key: "unsoldTokensDumpAddress",
      message: "Unsold Tokens Address is required.",
    },
    { key: "openTime", message: "Sale Start Time is required." },
    { key: "closeTime", message: "Sale End Time is required." },
    { key: "softCapInWei", message: "Softcap is required." },
    { key: "hardCapInWei", message: "Hardcap is required." },

    { key: "mainImg", message: "Main Image is required." },
    { key: "whitepaperLink", message: "Whitepaper Link is required." },
    { key: "description", message: "Description is required." },

    { key: "website", message: "Website is required." },
    { key: "telegram", message: "Telegram Username is required." },
    { key: "twitter", message: "Twitter Username is required." },
  ];

  if (poolType === "tokenSale") {
    fields.push({
      key: "minInvestInWei",
      message: "Min per Wallet is required.",
    });
    fields.push({
      key: "maxInvestInWei",
      message: "Max per Wallet is required.",
    });
  } else {
    fields = [
      ...fields,
      { key: "guaranteedHours", message: "HeadStart is required." },
      {
        key: "maxInvestInWei",
        message: "Allocation Factor is required.",
      },
      { key: "listingPriceInWei", message: "Listing Rate is required." },
      {
        key: "liquidityAddingTime",
        message: "Liquidity Listing Time is required.",
      },
      {
        key: "lpTokensLockDurationInDays",
        message: "Liquidity Lock Duration (Days) is required.",
      },
      {
        key: "liquidityPercentageAllocation",
        message: "Percentage Allocated To LP is required.",
      },
    ];
  }

  return fields;
};

const Create = ({ classes }) => {
  const theme = useTheme();
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [poolTitle, setPoolTitle] = useState("");
  const [tokenAddress, setTokenAddress] = useState("");
  const [tokenDecimals, setTokenDecimals] = useState(18);
  const [certified, setCertified] = useState(false);
  const [visible, setVisible] = useState(true);
  const [tokenImg, setTokenImg] = useState("");
  const [poolType, setPoolType] = useState("communityIdo");
  const [fundingTokenAddress, setFundingTokenAddress] = useState(
    defaultFundingAddress[theme.type]
  );

  const [tokenPriceInWei, setTokenPriceInWei] = useState("0");
  const [openTime, setOpenTime] = useState(
    Math.floor((Date.now() + 1 * 24 * 60 * 60 * 1000) / 1000)
  ); // start in 1 day
  const [closeTime, setCloseTime] = useState(
    Math.floor((Date.now() + 20 * 24 * 60 * 60 * 1000) / 1000)
  ); // end in 20 days

  const [listingPriceInWei, setListingPriceInWei] = useState("0");
  const [liquidityAddingTime, setLiquidityAddingTime] = useState(
    Math.floor((Date.now() + 20 * 24 * 60 * 60 * 1000) / 1000) + 1
  );
  const [lpTokensLockDurationInDays, setLpTokensLockDurationInDays] =
    useState(90);
  const [liquidityPercentageAllocation, setLiquidityPercentageAllocation] =
    useState(50);
  const history = useHistory();
  const { account } = useAccount();

  const [categoryId, setCategoryId] = useState(0);
  const [saleTypeId, setSaleTypeId] = useState(0);
  const [dexId, setDexId] = useState(0);
  const [guaranteedHours, setGuaranteedHours] = useState(1800);
  const [minStakeDays, setMinStakeDays] = useState(undefined);

  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [starterInfo, setStarterInfo] = useState(false);

  const [releaseAtTGE, setReleaseAtTGE] = useState(20);
  const [releaseCycle, setReleaseCycle] = useState(30 * 86400);
  const [releasePerCycle, setReleasePerCycle] = useState(20);
  const [releaseCliff, setReleaseCliff] = useState(0);

  const [unsoldTokensDumpAddress, setUnsoldTokensDumpAddress] = useState("");
  const [presaleId, setPresaleId] = useState(undefined);
  const [softCapInWei, setSoftCapInWei] = useState("0");
  const [hardCapInWei, setHardCapInWei] = useState("0");
  const [minInvestInWei, setMinInvestInWei] = useState("0.001");
  const [maxInvestInWei, setMaxInvestInWei] = useState("0");
  const [kycInformation, setKycInformation] = useState("");
  const [mainImg, setMainImg] = useState("");
  const [imgGallery, setImgGallery] = useState([]);
  const [description, setDescription] = useState("");
  const [whitepaperLink, setWhitepaperLink] = useState("");

  const [linkAudit, setLinkAudit] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [auditor, setAuditor] = useState("");

  const [website, setWebsite] = useState("");
  const [telegram, setTelegram] = useState("");
  const [github, setGithub] = useState("");
  const [twitter, setTwitter] = useState("");
  const [whitelist, setWhitelist] = useState([]);
  const [whitelistFile, setWhitelistFile] = useState("");
  const [investorsListFile, setInvestorsListFile] = useState("");
  const [investorsList, setInvestorsList] = useState([]);

  const web3 = new Web3();

  const stakingTokenName = store.getStore("StakingTokenName");

  const tokenImgInputRef = useRef(null);
  const mainImgInputRef = useRef(null);
  const imgGalleryInputRef = useRef(null);
  const whitelistInputRef = useRef(null);
  const investorsListInputRef = useRef(null);

  useEffect(() => {
    setVisible(poolType === "degenMode" ? false : true);
  }, [poolType]);

  useEffect(() => {
    emitter.on(APPLY_RETURNED, applyReturned);
    emitter.on(ERROR, errorReturned);

    return () => {
      emitter.removeListener(APPLY_RETURNED, applyReturned);
      emitter.removeListener(ERROR, errorReturned);
    };
  }, []);

  const errorReturned = (error) => {
    setShowLoadingModal(false);
    setShowSuccessModal(false);
  };

  const applyReturned = (bscId) => {
    setPresaleId(bscId);
    setShowSuccessModal(true);
    // setWarningMessage("Presale created successfully.");
    // history.push(`/pool/${presaleId}`);
  };

  const getStarterInfo = () => {
    dispatcher.dispatch({ type: GET_BALANCES, content: {} });
    dispatcher.dispatch({ type: GET_DEV_FEES, content: {} });
  };

  useEffect(() => {
    getStarterInfo();
    emitter.on(CONNECTION_CONNECTED, () => {
      getStarterInfo();
    });
    emitter.on(GET_DEV_FEES_RETURNED, getDevFeesReturned);
    emitter.on(GET_BALANCES_RETURNED, getBalancesReturned);
    emitter.on(
      GET_MIN_CREATOR_STAKED_BALANCE_RETURNED,
      getMinCreatorStakedBalReturned
    );

    return () => {
      emitter.removeListener(CONNECTION_CONNECTED, () => {
        getStarterInfo();
      });
      emitter.removeListener(GET_DEV_FEES_RETURNED, getDevFeesReturned);
      emitter.removeListener(GET_BALANCES_RETURNED, getBalancesReturned);
      emitter.removeListener(
        GET_MIN_CREATOR_STAKED_BALANCE_RETURNED,
        getMinCreatorStakedBalReturned
      );
    };
  }, []);

  useEffect(() => {
    const loadTokenDecimals = async () => {
      const themeType = store.getStore("themeType");
      const decimals = await getTokenDecimals(tokenAddress, themeType);

      setTokenDecimals(decimals);
    };

    if (tokenAddress) {
      loadTokenDecimals();
    }
  }, [tokenAddress]);

  const getMinCreatorStakedBalReturned = () => {
    const minCreatorStakedBalance = store.getStore("minCreatorStakedBalance");

    setStarterInfo((prev) => ({
      ...prev,
      minCreatorStakedBalance,
    }));
  };

  const getBalancesReturned = () => {
    const lastStakedTime = store.getStore("lastStakedTime");
    const minStakeTime = store.getStore("minStakeTime");

    const totalStakedBalance = store.getStore("totalStakedBalance");
    const isCertifiedAllowed = store.getStore("isCertifiedAllowed");

    setMinStakeDays(moment.duration(minStakeTime, "seconds").asDays());

    setStarterInfo((prev) => ({
      ...prev,
      lastStakedTime,
      minStakeTime,
      totalStakedBalance,
      isCertifiedAllowed,
    }));
  };

  useEffect(() => {
    dispatcher.dispatch({
      type: GET_MIN_CREATOR_STAKED_BALANCE,
      content: {
        fundingTokenAddress: fundingTokenAddress,
      },
    });
  }, [fundingTokenAddress]);

  const getDevFeesReturned = () => {
    const devFeePercentage = store.getStore("devFeePercentage");
    console.log({ devFeePercentage });

    setStarterInfo((prev) => ({
      ...prev,
      devFeePercentage,
    }));
  };

  useEffect(() => {
    if (account && account.address) setUnsoldTokensDumpAddress(account.address);
  }, [account]);

  const parseTimestampToString = (timestamp) => {
    return moment.unix(timestamp).format("YYYY-MM-DDTHH:mm");
  };

  const parseStringToTimestamp = (time) => {
    return moment(time, "yyyy-MM-DDThh:mm").unix();
  };

  const imageUpload = async (file) => {
    const url = `https://api.pinata.cloud/pinning/pinFileToIPFS`;
    const data = new FormData();
    data.append("file", file.raw);

    try {
      const headers = {
        "Content-Type": "multipart/form-data",
        pinata_api_key: process.env.PINATA_API_KEY ?? "d9f8f3c56a862e78a857",
        pinata_secret_api_key:
          process.env.PINATA_SECRET_API_KEY ??
          "9889a4e2d9c2c38f93f71e4be93bd10a36f79209557de006b34311ae4663e0b1",
      };

      const res = await api.post(url, data, {
        headers,
      });

      if (res.data) {
        try {
          const shortUrl = await shortenUrl(
            `https://gateway.pinata.cloud/ipfs/${res.data.IpfsHash}`
          );

          return shortUrl;
        } catch (err) {
          console.error("Error occurred while shorten image", err);
          throw new Error("Error occurred while shortening image");
        }
      }
    } catch (error) {
      console.error("Error occurred while uploading image", error);

      throw new Error("Error occurred while uploading image");
    }
  };

  const onApply = useCallback(async () => {
    try {
      setShowLoadingModal(true);

      const linkLogo = tokenImg ? await imageUpload(tokenImg) : "";
      const linkImgMain = mainImg ? await imageUpload(mainImg) : "";
      const linkImgAlt1 = imgGallery[0] ? await imageUpload(imgGallery[0]) : "";
      const linkImgAlt2 = imgGallery[1] ? await imageUpload(imgGallery[1]) : "";

      const whitepaper = await shortenUrl(whitepaperLink);

      const presaleStringInfoInput = {
        saleTitle: formatBytes32String(`${poolTitle}`),
        linkTelegram: formatBytes32String(
          `${telegram.replace(/@/g, "").trim()}`
        ),
        linkGithub: formatBytes32String(`${github.replace(/@/g, "").trim()}`),
        linkTwitter: formatBytes32String(`${twitter.replace(/@/g, "").trim()}`),
        linkWebsite: formatBytes32String(`${website.trim()}`),
        linkLogo: formatBytes32String(linkLogo),
        kycInformation: formatBytes32String(kycInformation),
        description: description.trim(),
        whitepaper: formatBytes32String(whitepaper),
        categoryId: categoryId,
        saleTypeId: saleTypeId,
        linkImgMain: formatBytes32String(linkImgMain),
        linkImgAlt1: formatBytes32String(linkImgAlt1),
        linkImgAlt2: formatBytes32String(linkImgAlt2),
      };

      const auditInfoInput = {
        auditor: formatBytes32String(auditor),
        linkAudit: linkAudit?.trim(),
        isVerified: isVerified,
        isWarning: isWarning,
      };

      const fundingToken = tokens[fundingTokenAddress];

      const investorAmounts = investorsList.map(
        (investor) =>
          toWeiString(investor.amount, fundingToken?.decimals || 18) || 0
      );
      const investorAddresses = investorsList.map(
        (investor) => investor.address
      );

      const presaleDetailsInput = {
        tokenAddress: tokenAddress || "",
        tokenDecimals: tokenDecimals,
        fundingTokenAddress: fundingTokenAddress,
        unsoldTokensDumpAddress: unsoldTokensDumpAddress,
        tokenPriceInWei:
          toWeiString(tokenPriceInWei, fundingToken?.decimals || 18) || 0,
        hardCapInWei:
          toWeiString(hardCapInWei, fundingToken?.decimals || 18) || 0,
        softCapInWei:
          toWeiString(softCapInWei, fundingToken?.decimals || 18) || 0,
        maxInvestInWei:
          toWeiString(maxInvestInWei, fundingToken?.decimals || 18) || 0,
        minInvestInWei:
          toWeiString(minInvestInWei, fundingToken?.decimals || 18) || 0,
        openTime: openTime || Math.floor(Date.now() / 1000),
        closeTime: closeTime || Math.floor(Date.now() / 1000),
        presaleType: presaleType[poolType],
        guaranteedHours:
          presaleType[poolType] === "0" ? 0 : parseInt(guaranteedHours),
        whitelistedAddresses: whitelist,
        investorAmounts: investorAmounts,
        investorAddresses: investorAddresses,
        releasePerCycle: parseInt(
          parseFloat(Number(releasePerCycle).toFixed(2)) * 100
        ),
        releaseCycle: parseInt(releaseCycle),
        releaseAtTGE: parseInt(
          parseFloat(Number(releaseAtTGE).toFixed(2)) * 100
        ),
        releaseCliff: parseInt(releaseCliff) * 86400,
        certified,
        visible,
      };

      const pancakeSwapInfoInput =
        presaleDetailsInput.presaleType === "0"
          ? {
              listingPriceInWei: toWeiString("0", fundingToken?.decimals || 18),
              liquidityAddingTime: presaleDetailsInput.closeTime + 1,
              lpTokensLockDurationInDays: 0,
              liquidityPercentageAllocation: 0,
              swapIndex: 0,
            }
          : {
              listingPriceInWei:
                toWeiString(listingPriceInWei, fundingToken?.decimals || 18) ||
                0,
              liquidityAddingTime: Number(liquidityAddingTime || 0) || 0,
              lpTokensLockDurationInDays:
                parseInt(lpTokensLockDurationInDays) * 86400 || 0,
              liquidityPercentageAllocation:
                Number(liquidityPercentageAllocation || 0) || 0,
              swapIndex: dexId,
            };

      console.log({
        bscPresaleInfo: presaleDetailsInput,
        presalePancakeSwapInfo: pancakeSwapInfoInput,
        presaleStringInfo: presaleStringInfoInput,
        auditInfo: auditInfoInput,
      });

      dispatcher.dispatch({
        type: APPLY,
        content: {
          bscPresaleInfo: presaleDetailsInput,
          presalePancakeSwapInfo: pancakeSwapInfoInput,
          presaleStringInfo: presaleStringInfoInput,
          auditInfo: auditInfoInput,
        },
      });
    } catch (error) {
      console.error("onApply", error);
      setErrorMessage("Unexpected error while creating a new project!");
      setShowLoadingModal(false);
    }
  }, [
    tokenImg,
    mainImg,
    imgGallery,
    poolTitle,
    telegram,
    github,
    twitter,
    website,
    kycInformation,
    description,
    whitepaperLink,
    categoryId,
    saleTypeId,
    dexId,
    auditor,
    linkAudit,
    isVerified,
    isWarning,
    fundingTokenAddress,
    releaseAtTGE,
    releaseCliff,
    investorsList,
    tokenAddress,
    tokenDecimals,
    unsoldTokensDumpAddress,
    tokenPriceInWei,
    hardCapInWei,
    softCapInWei,
    maxInvestInWei,
    minInvestInWei,
    openTime,
    closeTime,
    poolType,
    guaranteedHours,
    whitelist,
    releasePerCycle,
    releaseCycle,
    certified,
    visible,
    listingPriceInWei,
    liquidityAddingTime,
    lpTokensLockDurationInDays,
    liquidityPercentageAllocation,
  ]);

  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (error) {
      return false;
    }
  };

  const validateRequiredFields = useCallback(
    async (formState) => {
      const fieldsToValidate = getFieldsToValidate(poolType);

      for (let { key, message } of fieldsToValidate) {
        const value = formState[key];
        if (typeof value !== "number" && !value) {
          setErrorMessage(message);
          return false; // Stop validation on the first error
        }
      }

      // validate Ethereum addresses
      const ethereumAddresses = [
        {
          key: "unsoldTokensDumpAddress",
          message: "Unsold Tokens Address is not a valid Ethereum address.",
        },
        {
          key: "tokenAddress",
          message: "Token Address is not a valid Ethereum address.",
        },
      ];

      for (let { key, message } of ethereumAddresses) {
        const address = formState[key];
        if (!web3.utils.isAddress(address)) {
          setErrorMessage(message);
          return false;
        }
      }

      const urlFieldsToValidate = [
        {
          key: "whitepaperLink",
          required: true,
          message: "Whitepaper Link is not a valid URL.",
        },
        {
          key: "linkAudit",
          required: false,
          message: "Audit Link is not a valid URL.",
        },
        {
          key: "website",
          required: true,
          message: "Website is not a valid URL.",
        },
      ];

      // Validate URL fields
      for (let { key, required, message } of urlFieldsToValidate) {
        const urlValue = formState[key];
        if ((required && !urlValue) || (urlValue && !isValidUrl(urlValue))) {
          setErrorMessage(message);
          return false;
        }
      }

      if (
        poolType !== "communityIdo" &&
        Number(formState.hardCapInWei) < Number(formState.maxInvestInWei)
      ) {
        setErrorMessage(`Max per Wallet cannot exceed the Hardcap.`);
        return false;
      }

      if (Number(formState.hardCapInWei) < Number(formState.softCapInWei)) {
        setErrorMessage("Softcap cannot exceed the Hardcap.");
        return false;
      }

      if (
        poolType !== "communityIdo" &&
        Number(formState.maxInvestInWei) < Number(formState.minInvestInWei)
      ) {
        setErrorMessage("Min per Wallet cannot exceed Max per Wallet.");
        return false;
      }
      if (
        poolType !== "tokenSale" &&
        Number(formState.listingPriceInWei) < Number(formState.tokenPriceInWei)
      ) {
        setErrorMessage("Listing Rate must be higher or equal to Token Price.");
        return false;
      }

      if (
        moment(formState.openTime).isSameOrAfter(moment(formState.closeTime))
      ) {
        setErrorMessage("Sale Start Time must be before Sale End Time.");
        return false;
      }

      if (
        formState.liquidityAddingTime &&
        moment(formState.liquidityAddingTime).isSameOrBefore(
          moment(formState.closeTime)
        )
      ) {
        setErrorMessage("Liquidity Listing Time must be after Sale End Time.");
        return false;
      }

      const currentTimestamp = Math.floor(Date.now() / 1000);
      const timeSinceLastStake = currentTimestamp - starterInfo.lastStakedTime;
      if (timeSinceLastStake < starterInfo.minStakeTime) {
        setErrorMessage(
          `You must wait longer before attempting to create a project due to minimum stake time requirement.`
        );
        return false;
      }

      const [hasEnoughBalance, requiredBalance] = await validateUserBalance(
        account,
        formState.tokenAddress,
        store.getStore("themeType"),
        new Web3(store.getStore("web3context").library.provider),
        formState.tokenDecimals,
        formState.hardCapInWei,
        formState.liquidityPercentageAllocation,
        poolType === "tokenSale" ? "0" : formState.listingPriceInWei,
        formState.tokenPriceInWei
      );

      if (!hasEnoughBalance) {
        setErrorMessage(
          `You must have a BUIDL balance of at least ${requiredBalance} to create a project.`
        );
        return false;
      }

      if (
        bigInt(starterInfo.totalStakedBalance).lesser(
          starterInfo.minCreatorStakedBalance
        )
      ) {
        setErrorMessage(
          `You must at least ${starterInfo.minCreatorStakedBalance.toString()} staked to proceed.`
        );
        return false;
      }

      if (formState.certified && !starterInfo.isCertifiedAllowed) {
        setErrorMessage(
          `You cannot create certified projects without Starter's allowance.`
        );
        return false;
      }

      const investorsTotalAmounts = formState.investorsList.reduce(
        (acc, investor) => (acc += Number(investor.amount)),
        0
      );

      if (investorsTotalAmounts > Number(formState.hardCapInWei)) {
        setErrorMessage(`Investors Total Amounts cannot exceed the Hardcap.`);
        return false;
      }

      const investorAddresses = formState.investorsList.map(
        (investor) => investor.address
      );

      const uniqueInvestorAddresses = new Set(investorAddresses);
      if (uniqueInvestorAddresses.size !== investorAddresses.length) {
        setErrorMessage(
          `Duplicate investor address at found at investors list. Please ensure that each investor address is unique.`
        );
        return false;
      }

      if (formState.poolTitle.length > 30) {
        setErrorMessage(`Project Title should contain up to 30 characters.`);
        return false;
      }

      if (formState.website.length > 32) {
        setErrorMessage(`Website Link should contain up to 32 characters.`);
        return false;
      }

      return true;
    },
    [poolType, starterInfo, account, web3.utils]
  );

  const handleCreateProject = useCallback(async () => {
    const formState = {
      selectedNetwork,
      poolTitle,
      tokenAddress,
      tokenDecimals,
      tokenImg,
      fundingTokenAddress,
      releaseCycle,
      releaseAtTGE,
      releasePerCycle,
      releaseCliff,
      guaranteedHours,
      unsoldTokensDumpAddress,
      tokenPriceInWei,
      openTime,
      closeTime,
      softCapInWei,
      hardCapInWei,
      minInvestInWei,
      maxInvestInWei,
      mainImg,
      whitepaperLink,
      description,
      listingPriceInWei,
      liquidityAddingTime,
      lpTokensLockDurationInDays,
      liquidityPercentageAllocation,
      website,
      telegram,
      twitter,
      linkAudit,
      certified,
      investorsList,
    };
    const isValid = await validateRequiredFields(formState);
    if (isValid) {
      onApply();
    }
  }, [
    selectedNetwork,
    poolTitle,
    tokenAddress,
    tokenDecimals,
    tokenImg,
    fundingTokenAddress,
    releaseCycle,
    releaseAtTGE,
    releasePerCycle,
    releaseCliff,
    guaranteedHours,
    unsoldTokensDumpAddress,
    tokenPriceInWei,
    openTime,
    closeTime,
    softCapInWei,
    hardCapInWei,
    minInvestInWei,
    maxInvestInWei,
    mainImg,
    whitepaperLink,
    description,
    listingPriceInWei,
    liquidityAddingTime,
    lpTokensLockDurationInDays,
    liquidityPercentageAllocation,
    website,
    telegram,
    twitter,
    linkAudit,
    certified,
    investorsList,
    validateRequiredFields,
    onApply,
  ]);

  if (!selectedNetwork) {
    return (
      <div className={classes.root}>
        <div className={classes.networkContainer}>
          <Typography className={classes.poolsLabel}>
            Choose your network
          </Typography>
          <Typography className={classes.networkSubtitle}>
            Choose a primary blockchain to deploy your Project contracts on.
            <br /> You need to sign in for creation.
          </Typography>

          <div className={classes.networkInnerContent}>
            <div
              className={classes.networkContent}
              onClick={() => setSelectedNetwork("Base")}
            >
              <img src={BaseIcon} alt="Base" />
              <p>Base</p>
            </div>
            <div
              className={classes.networkContent}
              style={{ cursor: "default", opacity: "0.4" }}
              onClick={() => {
                // todo: programatically disable
                // setSelectedNetwork("PulseChain")
              }}
            >
              <img src={PulseIcon} alt="PulseChain" />
              <p>PulseChain</p>
            </div>
            <div
              className={classes.networkContent}
              style={{ cursor: "default", opacity: "0.4" }}
              onClick={() => {
                // todo: programatically disable
                // setSelectedNetwork("ethereum")
              }}
            >
              <img src={EthereumIcon} alt="Ethereum" />
              <p>Ethereum</p>
            </div>
            <div
              className={classes.networkContent}
              style={{ cursor: "default", opacity: "0.4" }}
              onClick={() => {
                // todo: programatically disable
                // setSelectedNetwork("polygon")
              }}
            >
              <img src={PolygonIcon} alt="Polygon" />
              <p>Polygon</p>
            </div>
            <div
              className={classes.networkContent}
              style={{ cursor: "default", opacity: "0.4" }}
              onClick={() => {
                // todo: programatically disable
                // setSelectedNetwork("bsc")
              }}
            >
              <img src={BscIcon} alt="Binance Smart Chain" />
              <p>Binance Smart Chain</p>
            </div>
            <div
              className={classes.networkContent}
              style={{ cursor: "default", opacity: "0.4" }}
              onClick={() => {
                // todo: programatically disable
                // setSelectedNetwork("avax")
              }}
            >
              <img src={AvaxIcon} alt="Avalanche" />
              <p>Avalanche</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.root}>
        {errorMessage && (
          <Snackbar
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={true}
            autoHideDuration={3000}
            onClick={() => {
              setErrorMessage("");
            }}
            onClose={() => {
              setErrorMessage("");
            }}
          >
            <Alert severity="error">{errorMessage}</Alert>
          </Snackbar>
        )}

        <div className={classes.poolsTitleContainer}>
          <Typography className={classes.poolsLabel}>Create Project</Typography>
          <Typography className={classes.subTitleText}>
            Project Details
          </Typography>
          <div className={classes.contentContainer}>
            <div className={classes.poolInnerContent}>
              <div>
                <p className={classes.inputLabel}>
                  Project Title <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={poolTitle}
                  onChange={(e) => {
                    if (
                      e.target !== undefined &&
                      e.target.value !== undefined
                    ) {
                      setPoolTitle(e.target.value);
                    }
                  }}
                />
              </div>

              <div className={classes.tokenInfoInnerContent}>
                <div>
                  <p className={classes.inputLabel}>
                    ERC20 Token Address{" "}
                    <span className={classes.inputRequired}>*</span>
                  </p>
                  <input
                    className={classes.textInput}
                    type="text"
                    value={tokenAddress}
                    onChange={(e) => {
                      setTokenAddress(e.target.value);
                    }}
                  />
                </div>
                <div>
                  <p className={classes.inputLabel}>
                    Token Decimals{" "}
                    <span className={classes.inputRequired}>*</span>
                  </p>
                  <input
                    className={classes.textInput}
                    type="text"
                    value={tokenDecimals}
                    onChange={(e) => {
                      if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                        setTokenDecimals(Number(e.target.value));
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classes.contentContainer}>
            <div className={classes.tokenImgInnerContent}>
              <div>
                <p className={classes.inputLabel}>
                  Token Image <span className={classes.inputRequired}>*</span>
                </p>
                <p className={classes.infoText}>
                  You can upload JPEG, JPG or PNG.
                </p>
                <p className={classes.infoText}>Max file size 2 MB.</p>

                <Button
                  classes={{
                    root: classes.imgGalleryBtn,
                    label: classes.buttonLabel,
                  }}
                  onClick={() => tokenImgInputRef.current.click()}
                >
                  Choose Image
                </Button>

                <input
                  className={classes.tokenImgInput}
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  ref={tokenImgInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setTokenImg({
                        preview: URL.createObjectURL(file),
                        raw: file,
                      });
                    } else {
                      e.target.value = "";
                    }
                  }}
                />
              </div>
              <div className={classes.tokenImgContainer}>
                {tokenImg && (
                  <>
                    <img
                      className={classes.tokenImg}
                      src={tokenImg.preview}
                      alt=""
                    />

                    <DeleteIcon
                      style={{
                        width: 26,
                        height: 26,
                        padding: "4px",
                        position: "absolute",
                        top: 26,
                        right: 26,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setTokenImg(undefined);

                        if (tokenImgInputRef.current) {
                          tokenImgInputRef.current.value = "";
                        }
                      }}
                    ></DeleteIcon>
                  </>
                )}
              </div>
            </div>
          </div>

          <Typography className={classes.subTitleText}>
            Choose pool type
          </Typography>
          <div className={classes.poolTypeContainer}>
            <div
              className={classes.poolTypeInnerContent}
              style={{
                border:
                  poolType === "tokenSale"
                    ? "2px solid " + theme?.colors?.wallet?.buttonBackground
                    : "2px solid" + theme?.colors?.createPool?.inputBorder,
              }}
              onClick={() => {
                setPoolType("tokenSale");
              }}
            >
              <img src="" alt="" srcset="" />
              <p className={classes.poolTypeTitle}>Token Sale</p>
              <p className={classes.infoText}>
                Regular token sale optionally limited to whitelisted addresses
                only. Requires community voting for approval.
              </p>
            </div>

            <div
              className={classes.poolTypeInnerContent}
              style={{
                border:
                  poolType === "communityIdo"
                    ? "2px solid " + theme?.colors?.wallet?.buttonBackground
                    : "2px solid" + theme?.colors?.createPool?.inputBorder,
              }}
              onClick={() => {
                setPoolType("communityIdo");
              }}
            >
              <img src="" alt="" srcset="" />
              <p className={classes.poolTypeTitle}>Community IDO</p>
              <p className={classes.infoText}>
                Raise funds from public investor community. Requires a
                percentage of the raise to be locked and listed on a DEX.
              </p>
            </div>

            <div
              className={classes.poolTypeInnerContent}
              style={{
                border:
                  poolType === "degenMode"
                    ? "2px solid " + theme?.colors?.wallet?.buttonBackground
                    : "2px solid" + theme?.colors?.createPool?.inputBorder,
              }}
              onClick={() => {
                setPoolType("degenMode");
              }}
            >
              <img src="" alt="" />
              <p className={classes.poolTypeTitle}>Degen Mode</p>
              <p className={classes.infoText}>
                Use our onchain smart contracts to create a fun non-voting,
                non-certified pool for your meme or degen community.
              </p>
            </div>

            <FormGroup className={classes.checkboxGroup}>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.customCheckbox}
                    checked={certified}
                    onChange={(e) => setCertified(e.target.checked)}
                    name="certified"
                  />
                }
                label="Is Certified BUIDLer"
              />
            </FormGroup>
          </div>

          <Typography className={classes.subTitleText}>
            Project Details
          </Typography>
          <div className={classes.contentContainer}>
            <p className={classes.inputLabel}>
              Token Price <span className={classes.inputRequired}>*</span>
            </p>
            <div className={classes.tokenPriceContainer}>
              <img
                className={classes.tokenNetworkImg}
                alt="token logo"
                src={tokens[fundingTokenAddress].logo}
              />
              <select
                className={classes.tokenPriceSelect}
                name="tokens"
                id="tokens"
                value={fundingTokenAddress}
                onChange={(e) => {
                  setFundingTokenAddress(e.target.value);
                }}
              >
                {fundingTokens[theme.type].map((token) => (
                  <option key={token.value} value={token.value}>
                    {token.label}
                  </option>
                ))}
              </select>
              <input
                className={classes.tokenPriceInput}
                type="text"
                placeholder="0.00"
                value={tokenPriceInWei}
                onChange={(e) => {
                  if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                    setTokenPriceInWei(e.target.value);
                  }
                }}
              />
              <p className={classes.tokenPriceInUSD}>$0.00 USD</p>
            </div>
            <p className={classes.infoText}>
              Developer fee:{" "}
              {starterInfo?.devFeePercentage &&
              !isNaN(Number(presaleType[poolType])) &&
              Object.keys(starterInfo.devFeePercentage).includes(
                String(presaleType[poolType])
              )
                ? starterInfo.devFeePercentage[String(presaleType[poolType])] ??
                  10
                : 10}
              % of amount raised
            </p>
          </div>

          <div className={classes.contentContainer}>
            <div className={classes.infoInnerContent}>
              <div>
                <p className={classes.inputLabel}>
                  Release Cycle <span className={classes.inputRequired}>*</span>
                </p>

                <select
                  className={classes.categorySelect}
                  name="releaseCycle"
                  id="releaseCycle"
                  value={releaseCycle}
                  onChange={(e) => {
                    setReleaseCycle(Number(e.target.value));
                  }}
                >
                  {releaseCycles.map((releaseCycle) => (
                    <option key={releaseCycle.label} value={releaseCycle.value}>
                      {releaseCycle.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Release at TGE (%){" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={releaseAtTGE}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                      if (Number(value) > 100) value = "100";
                      if (Number(value) < 0) value = "0";

                      setReleaseAtTGE(value);
                    }
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Release Per Cycle (%){" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={releasePerCycle}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                      if (Number(value) > 100) value = "100";
                      if (Number(value) < 0) value = "0";

                      setReleasePerCycle(value);
                    }
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Vesting Cliff (Days){" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={releaseCliff}
                  onChange={(e) => {
                    if (/^[0-9]*$/.test(e.target.value)) {
                      setReleaseCliff(Number(e.target.value));
                    }
                  }}
                />
              </div>

              {poolType !== "tokenSale" && (
                <>
                  <div>
                    <p className={classes.inputLabel}>
                      HeadStart <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={guaranteedHours}
                      onChange={(e) => {
                        if (/^[0-9]*$/.test(e.target.value)) {
                          setGuaranteedHours(e.target.value);
                        }
                      }}
                    />
                  </div>

                  <div>
                    <p className={classes.inputLabel}>
                      Unsold Tokens Address{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={unsoldTokensDumpAddress}
                      onChange={(e) => {
                        setUnsoldTokensDumpAddress(e.target.value);
                      }}
                    />
                  </div>
                </>
              )}

              <div>
                <p className={classes.inputLabel}>
                  Sale Start Time{" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <TextField
                  className={classes.inputDatetime}
                  id="datetime-local"
                  type="datetime-local"
                  value={parseTimestampToString(openTime)}
                  onChange={(e) => {
                    const timestamp = parseStringToTimestamp(e.target.value);
                    setOpenTime(timestamp);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Sale End Time <span className={classes.inputRequired}>*</span>
                </p>
                <TextField
                  className={classes.inputDatetime}
                  id="datetime-local"
                  type="datetime-local"
                  value={parseTimestampToString(closeTime)}
                  onChange={(e) => {
                    const timestamp = parseStringToTimestamp(e.target.value);

                    setCloseTime(timestamp);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Softcap <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={softCapInWei}
                  onChange={(e) => {
                    if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setSoftCapInWei(e.target.value);
                    }
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Hardcap <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={hardCapInWei}
                  onChange={(e) => {
                    if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setHardCapInWei(e.target.value);
                    }
                  }}
                />
              </div>
              {poolType !== "communityIdo" ? (
                <>
                  <div>
                    <p className={classes.inputLabel}>
                      Min per Wallet{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={minInvestInWei}
                      onChange={(e) => {
                        if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                          setMinInvestInWei(e.target.value);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <p className={classes.inputLabel}>
                      Max per Wallet{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={maxInvestInWei}
                      onChange={(e) => {
                        if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                          setMaxInvestInWei(e.target.value);
                        }
                      }}
                    />
                  </div>

                  <div>
                    <p className={classes.inputLabel}>
                      Unsold Tokens Address{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={unsoldTokensDumpAddress}
                      onChange={(e) => {
                        setUnsoldTokensDumpAddress(e.target.value);
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className={classes.inputLabel}>
                      Allocation Factor{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={maxInvestInWei}
                      onChange={(e) => {
                        if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                          setMaxInvestInWei(e.target.value);
                        }
                      }}
                    />
                  </div>
                </>
              )}

              {poolType !== "tokenSale" && (
                <div>
                  <p className={classes.inputLabel}>DEX</p>
                  <select
                    className={classes.categorySelect}
                    name="dex"
                    id="dex"
                    value={dexId}
                    onChange={(e) => {
                      setDexId(Number(e.target.value));
                    }}
                  >
                    {dexIds.map((dex, index) => (
                      <option key={dex} value={index}>
                        {dex}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <FormGroup
              style={{ marginTop: "20px" }}
              className={classes.checkboxGroup}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.customCheckbox}
                    checked={visible}
                    onChange={(e) => setVisible(e.target.checked)}
                    name="visible"
                  />
                }
                label="Publicly Accessible"
              />
            </FormGroup>
          </div>

          <Typography className={classes.subTitleText}>
            Project Details
          </Typography>
          <div className={classes.contentContainer}>
            <div className={classes.projectInnerContent}>
              <div>
                <p className={classes.inputLabel}>
                  Main Image <span className={classes.inputRequired}>*</span>
                </p>
                <p className={classes.infoText}>
                  You can upload JPEG, JPG or PNG.
                </p>
                <p className={classes.infoText}>Max file size 2 MB.</p>

                <Button
                  classes={{
                    root: classes.imgGalleryBtn,
                    label: classes.buttonLabel,
                  }}
                  onClick={() => mainImgInputRef.current.click()}
                >
                  Choose Image
                </Button>

                <input
                  className={classes.mainImgInput}
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  ref={mainImgInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setMainImg({
                        preview: URL.createObjectURL(file),
                        raw: file,
                      });
                    } else {
                      e.target.value = "";
                    }
                  }}
                />
              </div>

              <div className={classes.mainImgContainer}>
                {mainImg && (
                  <>
                    <img
                      className={classes.mainImg}
                      src={mainImg.preview}
                      alt=""
                    />
                    <DeleteIcon
                      style={{
                        width: 26,
                        height: 26,
                        padding: "4px",
                        position: "absolute",
                        top: 3,
                        right: 3,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setMainImg(undefined);
                        if (mainImgInputRef.current) {
                          mainImgInputRef.current.value = "";
                        }
                      }}
                    ></DeleteIcon>
                  </>
                )}
              </div>
            </div>

            <p className={classes.inputLabel}>Image Gallery</p>
            <p className={classes.infoText}>You can add up to 5 more images.</p>
            <p className={classes.infoText}>You can upload JPEG, JPG or PNG.</p>
            <p className={classes.infoText}>Max file size 2 MB.</p>
            <Button
              classes={{
                root: classes.imgGalleryBtn,
                label: classes.buttonLabel,
              }}
              onClick={() => imgGalleryInputRef.current.click()}
            >
              Choose Image
            </Button>

            <input
              className={classes.galleryImgInput}
              type="file"
              accept=".jpeg, .jpg, .png"
              ref={imgGalleryInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && imgGallery.length < 2) {
                  setImgGallery((prevState) => [
                    ...prevState,
                    {
                      preview: URL.createObjectURL(file),
                      raw: file,
                    },
                  ]);
                } else {
                  e.target.value = "";
                }
              }}
            />

            <div className={classes.galleryImgGrid}>
              <div className={classes.galleryImgContainer}>
                {imgGallery.length >= 1 && imgGallery[0] && (
                  <>
                    <img
                      className={classes.galleryImg}
                      src={imgGallery[0].preview}
                      alt=""
                    />

                    <DeleteIcon
                      style={{
                        width: 18,
                        height: 18,
                        padding: "3px",
                        position: "absolute",
                        top: 2,
                        right: 2,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setImgGallery((prevState) =>
                          prevState.filter(
                            (img) => img.preview !== imgGallery[0].preview
                          )
                        );

                        if (imgGalleryInputRef.current) {
                          imgGalleryInputRef.current.value = "";
                        }
                      }}
                    ></DeleteIcon>
                  </>
                )}
              </div>
              <div className={classes.galleryImgContainer}>
                {imgGallery.length >= 2 && imgGallery[1] && (
                  <>
                    <img
                      className={classes.galleryImg}
                      src={imgGallery[1].preview}
                      alt=""
                    />
                    <DeleteIcon
                      style={{
                        width: 18,
                        height: 18,
                        padding: "3px",
                        position: "absolute",
                        top: 2,
                        right: 2,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setImgGallery((prevState) =>
                          prevState.filter(
                            (img) => img.preview !== imgGallery[1].preview
                          )
                        );
                        if (imgGalleryInputRef.current) {
                          imgGalleryInputRef.current.value = "";
                        }
                      }}
                    ></DeleteIcon>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={classes.contentContainer}>
            <div className={classes.contactInnerContent}>
              <div>
                <p className={classes.inputLabel}>
                  Whitepaper Link{" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={whitepaperLink}
                  onChange={(e) => {
                    setWhitepaperLink(e.target.value);
                  }}
                />
              </div>

              <div>
                <p className={classes.inputLabel}>KYC Information</p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={kycInformation}
                  onChange={(e) => {
                    setKycInformation(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className={classes.descriptionContainer}>
              <p className={classes.inputLabel}>
                Description <span className={classes.inputRequired}>*</span>
              </p>

              <textarea
                className={classes.descriptionTextArea}
                cols="30"
                rows="10"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
              ></textarea>
            </div>
          </div>

          <Typography className={classes.subTitleText}>
            Audit Details
          </Typography>
          <div className={classes.contentContainer}>
            <div className={classes.contactInnerContent}>
              <div>
                <p className={classes.inputLabel}>Company</p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={auditor}
                  onChange={(e) => {
                    setAuditor(e.target.value);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>Audit URL</p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={linkAudit}
                  onChange={(e) => {
                    setLinkAudit(e.target.value);
                  }}
                />
              </div>

              <FormGroup className={classes.checkboxGroup}>
                <FormControlLabel
                  control={
                    <Radio
                      className={classes.customRadio}
                      checked={isVerified}
                      onChange={() => {
                        setIsWarning(false);
                        setIsVerified(true);
                      }}
                      value="isVerified"
                      name="verified-radio-button"
                    />
                  }
                  label="Passed, No Issues"
                />
              </FormGroup>
              <FormGroup className={classes.checkboxGroup}>
                <FormControlLabel
                  control={
                    <Radio
                      className={classes.customRadio}
                      checked={isWarning}
                      onChange={() => {
                        setIsWarning(true);
                        setIsVerified(false);
                      }}
                      value="isWarning"
                      name="warning-radio-button"
                    />
                  }
                  label="Passed, w/ Notices"
                />
              </FormGroup>
            </div>
          </div>

          {poolType !== "tokenSale" && (
            <>
              <Typography className={classes.subTitleText}>
                Listing Info
              </Typography>
              <div className={classes.contentContainer}>
                <div className={classes.listingInnerContent}>
                  <div>
                    <p className={classes.inputLabel}>
                      Listing Rate{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={listingPriceInWei}
                      onChange={(e) => {
                        if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                          setListingPriceInWei(e.target.value);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <p className={classes.inputLabel}>
                      Liquidity Listing Time{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>

                    <TextField
                      className={classes.inputDatetime}
                      id="datetime-local"
                      type="datetime-local"
                      value={parseTimestampToString(liquidityAddingTime)}
                      onChange={(e) => {
                        const timestamp = parseStringToTimestamp(
                          e.target.value
                        );
                        setLiquidityAddingTime(timestamp);
                      }}
                    />
                  </div>
                  <div>
                    <p className={classes.inputLabel}>
                      Liquidity Lock Duration (Days){" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={lpTokensLockDurationInDays}
                      onChange={(e) => {
                        if (/^[0-9]*$/.test(e.target.value)) {
                          setLpTokensLockDurationInDays(e.target.value);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <p className={classes.inputLabel}>
                      Percentage Allocated To LP{" "}
                      <span className={classes.inputRequired}>*</span>
                    </p>
                    <input
                      className={classes.textInput}
                      type="text"
                      value={liquidityPercentageAllocation}
                      onChange={(e) => {
                        let value = e.target.value;

                        if (/^[0-9.]*$/.test(value)) {
                          if (Number(value) > 100) value = "100";
                          if (Number(value) < 0) value = "0";
                          if (Number(value) === 0) value = "0";

                          setLiquidityPercentageAllocation(value);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <Typography className={classes.subTitleText}>
            Contact Details
          </Typography>
          <div className={classes.contentContainer}>
            <div className={classes.contactInnerContent}>
              <div>
                <p className={classes.inputLabel}>Category</p>
                <select
                  className={classes.categorySelect}
                  name="category"
                  id="category"
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(Number(e.target.value));
                  }}
                >
                  {categoryIds.map((category, index) => (
                    <option key={category} value={index}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className={classes.inputLabel}>Sale Type</p>
                <select
                  className={classes.categorySelect}
                  name="saleType"
                  id="saleType"
                  value={saleTypeId}
                  onChange={(e) => {
                    setSaleTypeId(Number(e.target.value));
                  }}
                >
                  {saleTypeIds.map((saleType, index) => (
                    <option key={saleType} value={index}>
                      {saleType}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className={classes.inputLabel}>
                  Website <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Telegram Username{" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={telegram}
                  onChange={(e) => {
                    setTelegram(e.target.value);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>Github Username</p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={github}
                  onChange={(e) => {
                    setGithub(e.target.value);
                  }}
                />
              </div>
              <div>
                <p className={classes.inputLabel}>
                  Twitter Username{" "}
                  <span className={classes.inputRequired}>*</span>
                </p>
                <input
                  className={classes.textInput}
                  type="text"
                  value={twitter}
                  onChange={(e) => {
                    setTwitter(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          <Typography className={classes.subTitleText}>
            Private Project
          </Typography>

          <div className={classes.privateProjectContainer}>
            <div>
              <p className={classes.inputLabel}>Whitelist</p>
              <p className={classes.infoText}>
                You can limit access to this pool to a list of whitelisted
                addresses. CSV format only (max file size 2 MB)
              </p>

              <div className={classes.whitelistContainer}>
                <Button
                  classes={{
                    root: classes.whitelistBtn,
                    label: classes.buttonLabel,
                  }}
                  onClick={() => whitelistInputRef.current.click()}
                >
                  Choose Whitelist
                </Button>
                {whitelistFile?.name && (
                  <div className={classes.whitelistFileNameContainer}>
                    <p>{whitelistFile?.name}</p>
                    <DeleteIcon
                      style={{
                        width: 26,
                        height: 26,
                        padding: "4px",
                        top: 26,
                        right: 26,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setWhitelistFile(undefined);
                        setWhitelist([]);

                        if (whitelistInputRef.current) {
                          whitelistInputRef.current.value = "";
                        }
                      }}
                    ></DeleteIcon>
                  </div>
                )}
              </div>

              <input
                className={classes.whitelistImgInput}
                type="file"
                accept=".csv"
                ref={whitelistInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const text = e.target.result;
                      // Split the CSV content into rows and then extract addresses from each row
                      const rows = text.split("\n").filter(Boolean).slice(1);
                      let allAddressesValid = true;

                      const addresses = rows.map((row) => {
                        // Assuming each row contains a single Ethereum address
                        const columns = row.split(",");
                        return columns[0].trim(); // Adjust this if the address is not in the first column
                      });

                      // Validate each Ethereum address
                      for (let address of addresses) {
                        if (!web3.utils.isAddress(address)) {
                          allAddressesValid = false;
                          break; // Exit the loop if an invalid address is found
                        }
                      }

                      console.log({ addresses });

                      if (allAddressesValid) {
                        setWhitelist(addresses);
                        setWhitelistFile(file);
                      } else {
                        setErrorMessage(
                          "The CSV file contains one or more invalid Ethereum addresses."
                        );
                      }
                    };

                    reader.readAsText(file);
                  } else {
                    e.target.value = "";
                  }
                }}
              />
            </div>

            <div>
              <p className={classes.inputLabel}>Investors List</p>
              <p className={classes.infoText}>
                If funds were accepted externally, upload a list of addresses
                and amounts for distribution. CSV format only (max file size 2
                MB)
              </p>

              <div className={classes.whitelistContainer}>
                <Button
                  classes={{
                    root: classes.whitelistBtn,
                    label: classes.buttonLabel,
                  }}
                  onClick={() => investorsListInputRef.current.click()}
                >
                  Choose Investors List
                </Button>
                {investorsListFile?.name && (
                  <div className={classes.whitelistFileNameContainer}>
                    <p>{investorsListFile?.name}</p>
                    <DeleteIcon
                      style={{
                        width: 26,
                        height: 26,
                        padding: "4px",
                        top: 26,
                        right: 26,
                        cursor: "pointer",
                        borderRadius: "100%",
                        backgroundColor:
                          theme?.colors?.wallet?.buttonBackground,
                        color: theme?.colors?.wallet?.buttonTextSecondary,
                        boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
                      }}
                      onClick={() => {
                        setInvestorsListFile(undefined);
                        setInvestorsList([]);
                        if (investorsListInputRef.current) {
                          investorsListInputRef.current.value = ""; // Clear the input's value
                        }
                      }}
                    ></DeleteIcon>
                  </div>
                )}
              </div>

              <input
                className={classes.whitelistImgInput}
                type="file"
                accept=".csv"
                ref={investorsListInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const text = e.target.result;

                      // Split the CSV content into rows and then extract addresses and amounts from each row
                      const rows = text.split("\n").filter(Boolean).slice(1);
                      let errorMessage = ""; // Initialize an empty error message

                      const investors = rows.map((row) => {
                        const columns = row
                          .split(",")
                          .map((column) => column.trim());
                        return {
                          address: columns[0],
                          amount: columns[1],
                        };
                      });

                      // Validate each investor data
                      for (const { address, amount } of investors) {
                        // Validate Ethereum address
                        if (!web3.utils.isAddress(address)) {
                          errorMessage =
                            "The CSV file contains an invalid Ethereum address.";
                          break; // Stop checking further
                        }

                        // Validate amount as a positive number
                        if (isNaN(amount) || parseFloat(amount) <= 0) {
                          errorMessage =
                            "The CSV file contains an invalid amount.";
                          break; // Stop checking further
                        }
                      }

                      console.log({ investors });

                      if (errorMessage) {
                        setErrorMessage(errorMessage);
                      } else {
                        setInvestorsListFile(file);
                        setInvestorsList(investors);
                      }
                    };
                    reader.readAsText(file);
                  } else {
                    e.target.value = "";
                  }
                }}
              />
            </div>
          </div>

          <Button
            classes={{
              root: classes.createButton,
              label: classes.buttonLabel,
            }}
            onClick={handleCreateProject}
          >
            Create Project
          </Button>
        </div>

        <div className={classes.projectRequirements}>
          <div style={{ height: "65px" }} />

          <Typography className={classes.subTitleText}>
            Project Creation Requirements
          </Typography>
          <div className={classes.contentContainer}>
            <ul className={classes.voteDescription}>
              <li>
                <Typography variant="h3" className={classes.voteTipText}>
                  <strong>Minimum Stake Time:</strong> Ensure that the minimum
                  time {`(${minStakeDays} day${minStakeDays !== 1 ? "s" : ""})`}{" "}
                  since your last stake has passed before attempting to create a
                  project. If you've staked recently, please wait longer to meet
                  the minimum stake time requirement.
                </Typography>
              </li>
              <li>
                <Typography variant="h3" className={classes.voteTipText}>
                  <strong>Minimum Staked Balance:</strong> Confirm that your
                  total staked balance meets or exceeds the minimum required{" "}
                  <strong>
                    {starterInfo.minCreatorStakedBalance &&
                      bigInt(starterInfo.minCreatorStakedBalance).greater(0) &&
                      `(${toFixed(
                        starterInfo.minCreatorStakedBalance
                      )} ${stakingTokenName})`}
                  </strong>{" "}
                  for project creators.
                </Typography>
              </li>
              <li>
                <Typography variant="h3" className={classes.voteTipText}>
                  <strong>Sufficient ERC20 Token Balance:</strong> Verify that
                  your account holds at least the required balance to create a
                  project. This balance is needed to cover the project's hard
                  cap, liquidity percentage allocation, listing price, and token
                  price.
                </Typography>
              </li>
              <li>
                <Typography variant="h3" className={classes.voteTipText}>
                  <strong>Certification Allowance:</strong> For certified
                  projects, ensure you have Starter's allowance to create such
                  projects. You can apply for a certification{" "}
                  <a
                    href="https://form.typeform.com/to/NPvV8s53"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </Typography>
              </li>
            </ul>
          </div>
        </div>

        <ConfirmationModal
          modalOpen={showLoadingModal}
          onClose={() => {
            if (showSuccessModal) {
              history.push(`/pool/${presaleId}`);
            }

            setShowLoadingModal(false);
            setShowSuccessModal(false);
          }}
          showSuccessModal={showSuccessModal}
        />
      </div>
    </>
  );
};

export default withNamespaces()(withRouter(withStyles(styles)(Create)));
